import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { useJobStore } from "../../stores/jobStore";

export function AiIntakePanel() {
  const { aiIntakeProgress, selectedJob, pauseAiIntake, resumeAiIntake } = useJobStore();
  const [showLog, setShowLog] = useState(false);
  const [acting, setActing] = useState(false);
  const [restored, setRestored] = useState<{
    pages_done: number; last_page: number; charts_found: number; total: number;
  } | null>(null);

  const isAiJob = selectedJob &&
    ["RUNNING", "AI_INTAKE_PAUSED", "INTAKE_COMPLETE", "QUEUED"].includes(selectedJob.status);

  // Restore progress from backend when aiIntakeProgress is null but job is active
  useEffect(() => {
    if (!selectedJob || aiIntakeProgress) {
      setRestored(null);
      return;
    }
    if (!["RUNNING", "AI_INTAKE_PAUSED"].includes(selectedJob.status)) {
      setRestored(null);
      return;
    }
    api.intakeAi.getProgress(String(selectedJob.id)).then(setRestored).catch(() => {});
  }, [selectedJob?.id, selectedJob?.status, aiIntakeProgress]);

  if (!isAiJob) return null;

  // Use live WS data if available, else fall back to restored snapshot
  const live = aiIntakeProgress;
  const page = live?.page ?? restored?.last_page ?? 0;
  const total = live?.total ?? restored?.total ?? 0;
  const chartsFound = live?.chartsFound ?? restored?.charts_found ?? 0;
  const message = live?.message ?? (restored ? `صفحه ${restored.last_page} از ${restored.total} پردازش شده` : "در حال بارگذاری…");
  const classifyModel = live?.classifyModel ?? "";
  const semanticsModel = live?.semanticsModel ?? "";
  const phase = live?.phase ?? (selectedJob.status === "AI_INTAKE_PAUSED" ? "paused" : "classify");
  const logLines = live?.log ?? [];
  const currentSection = live?.currentSection;
  const semanticsCurrent = live?.semanticsCurrent;
  const semanticsTotal = live?.semanticsTotal;

  const pct = total > 0 ? Math.round((page / total) * 100) : 0;
  // status from DB is authoritative — AI_INTAKE_PAUSED always wins over stale live phase
  const isPaused = selectedJob.status === "AI_INTAKE_PAUSED" || phase === "paused";
  const isError  = !isPaused && phase === "error";
  const isDone   = !isPaused && (phase === "done" || selectedJob.status === "INTAKE_COMPLETE");
  const isSem    = !isPaused && phase === "semantics";

  const _phaseLabels: Record<string, string> = {
    classify:  "Pass 1 — تشخیص صفحه",
    semantics: "Pass 2 — تولید سمنتیک",
    done:      "کامل شد ✓",
    error:     "خطا",
  };
  const phaseLabel = isPaused ? "متوقف — کلیک Resume برای ادامه" :
    isDone ? "کامل شد ✓" : isError ? "خطا" :
    (_phaseLabels[phase] ?? phase);

  const phaseColor = isPaused ? "text-orange-400" : isDone ? "text-emerald-400" : isError
    ? "text-red-400" : phase === "semantics" ? "text-teal-400" : "text-blue-400";

  const handlePause = async () => {
    if (acting) return;
    setActing(true);
    try { await pauseAiIntake(String(selectedJob!.id)); }
    finally { setActing(false); }
  };

  const handleResume = async () => {
    if (acting) return;
    setActing(true);
    try { await resumeAiIntake(String(selectedJob!.id)); }
    finally { setActing(false); }
  };

  return (
    <div className="flex-shrink-0 border-b border-rdp-border bg-rdp-bg/80">
      {/* Top bar */}
      <div className="px-5 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {!isDone && !isPaused && !isError && (
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse flex-shrink-0" />
          )}
          <span className={`text-[11px] font-semibold ${phaseColor} flex-shrink-0`}>{phaseLabel}</span>
          <span className="text-[11px] text-slate-500 truncate">{message}</span>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {logLines.length > 0 && (
            <button
              onClick={() => setShowLog(v => !v)}
              className="text-[10px] text-slate-600 hover:text-slate-300 transition-colors cursor-pointer px-2 py-1 rounded border border-rdp-border/40"
            >
              {showLog ? "▲ لاگ" : "▼ لاگ"}
            </button>
          )}

          {!isDone && !isPaused && !isError && (
            <button
              onClick={handlePause}
              disabled={acting}
              className="text-[11px] font-medium px-3 py-1 rounded bg-orange-500/15 border border-orange-500/30 text-orange-300 hover:bg-orange-500/25 transition-all cursor-pointer disabled:opacity-50"
            >
              ⏸ پاوز
            </button>
          )}
          {(isPaused || isError) && (
            <button
              onClick={handleResume}
              disabled={acting}
              className="text-[11px] font-medium px-3 py-1 rounded bg-blue-500/15 border border-blue-500/30 text-blue-300 hover:bg-blue-500/25 transition-all cursor-pointer disabled:opacity-50"
            >
              {acting ? "…" : "▶ ادامه"}
            </button>
          )}
        </div>
      </div>

      {/* Progress bars */}
      <div className="px-5 pb-3 flex flex-col gap-2">
        <div>
          <div className="flex justify-between text-[10px] text-slate-600 mb-1">
            <span>Pass 1 — صفحه {page} از {total || "?"}</span>
            <span className="font-mono">{chartsFound} نمودار · {pct}%</span>
          </div>
          <div className="h-1.5 bg-rdp-border rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                isDone ? "bg-emerald-500" : isPaused ? "bg-orange-500" :
                isError ? "bg-red-500" : "bg-gradient-to-r from-blue-600 to-blue-400"
              }`}
              style={{ width: `${Math.min(100, pct)}%` }}
            />
          </div>
        </div>

        {(isSem || isDone) && semanticsTotal != null && (
          <div>
            <div className="flex justify-between text-[10px] text-slate-600 mb-1">
              <span>Pass 2 — سمنتیک Section {currentSection ?? "…"}</span>
              <span className="font-mono">{semanticsCurrent ?? 0}/{semanticsTotal}</span>
            </div>
            <div className="h-1 bg-rdp-border rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-teal-600 to-teal-400 rounded-full transition-all duration-500"
                style={{ width: `${semanticsTotal > 0 ? Math.round(((semanticsCurrent ?? 0) / semanticsTotal) * 100) : 0}%` }}
              />
            </div>
          </div>
        )}

        {(classifyModel || semanticsModel) && (
          <div className="flex gap-4 text-[10px] text-slate-600">
            {classifyModel && <span>Vision: <span className="font-mono text-slate-400">{classifyModel}</span></span>}
            {semanticsModel && <span>Semantics: <span className="font-mono text-slate-400">{semanticsModel}</span></span>}
          </div>
        )}
      </div>

      {showLog && logLines.length > 0 && (
        <div className="px-5 pb-3">
          <div className="rounded-lg bg-rdp-bg border border-rdp-border max-h-40 overflow-y-auto p-2.5 flex flex-col gap-1">
            {logLines.map((line, i) => (
              <p key={i} className={`text-[10px] font-mono leading-tight ${
                line.includes("خطا") || line.includes("Error") ? "text-red-400" :
                line.includes("✓") ? "text-emerald-400" : "text-slate-500"
              }`}>
                {line}
              </p>
            ))}
          </div>
        </div>
      )}

      {isError && message && (
        <div className="px-5 pb-3">
          <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-[11px] text-red-300 whitespace-pre-wrap">
            {message}
          </div>
        </div>
      )}
    </div>
  );
}
